import React, { useState, useEffect } from "react";

// Components
import Table from "./Childs/Table";
import FormCard from "../../Common/Childs/Structure/FormCard";
import Description from "../../Common/Childs/Structure/Description";
import Title from "../../Common/Childs/Structure/Title";
import Form from "./Childs/Form";
import Delete from "./Delete";
import { useGlobal } from "../../../context/GlobalContextProvider";

// Image for the add button
import plus from "../../../assets/icons/plus-solid.svg";

// Initial form state
const INITIAL_FORM_STATE = {
  id: 0,
  rank: "",
  price: "",
  color: "",
  delete: false,
};

/**
 * API service for background price operations
 */
const backgroundPriceAPI = {
  getAll: (campaignId) => fetch(`/api/campaign/${campaignId}/backgroundprice`),
  create: (campaignId, data) =>
    fetch(`/api/campaign/${campaignId}/backgroundprice`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
  update: (campaignId, id, data) =>
    fetch(`/api/campaign/${campaignId}/backgroundprice/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
  delete: (campaignId, id) =>
    fetch(`/api/campaign/${campaignId}/backgroundprice/${id}`, {
      method: "DELETE",
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
};

/**
 * API service for background operations
 */
const backgroundAPI = {
  getByArtPack: (artPackId) => fetch(`/api/artpack/${artPackId}/background`),
};

/**
 * BackgroundPrice Component
 * Manages pricing levels based on NFT background rarity
 */
const Index = () => {
  const { campaign, artPack, setSpinner } = useGlobal();

  const [data, setData] = useState({ items: [], cryptoPrice: null });
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [rankList, setRankList] = useState([]);
  const [rankColorMap, setRankColorMap] = useState({});

  /**
   * Fetch background price data
   */
  useEffect(() => {
    const loadTableData = async () => {
      if (!campaign) return;

      setSpinner(true);
      try {
        const res = await backgroundPriceAPI.getAll(campaign.campaign_id);
        if (res.status === 200) {
          const json = await res.json();
          setData({ items: json.data, cryptoPrice: json.cryptoPrice });
        }
      } catch (error) {
        console.error("Error loading background price data:", error);
      } finally {
        setSpinner(false);
      }
    };

    loadTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  /**
   * Fetch rank list and color mapping
   */
  useEffect(() => {
    const getRankList = async () => {
      if (!artPack) return;

      try {
        const res = await backgroundAPI.getByArtPack(artPack.art_pack_id);
        if (res.status === 200) {
          const json = await res.json();

          const mappedData = json.map((item) => ({
            background_id: item.background_id,
            rank: item.rank,
            name: item.color_hex_code,
          }));

          const colorMap = json.reduce((acc, item) => {
            acc[item.rank] = item.color_hex_code;
            return acc;
          }, {});

          setRankList(mappedData);
          setRankColorMap(colorMap);
        }
      } catch (error) {
        console.error("Error loading rank list:", error);
      }
    };

    getRankList();
  }, [artPack]);

  /**
   * Show form for adding new item
   */
  const handleAdd = () => {
    setShowForm(true);
    setFormData(INITIAL_FORM_STATE);
  };

  /**
   * Show form for editing item
   */
  const handleEdit = (item) => {
    setShowForm(true);
    setFormData({
      id: item.background_price_id,
      rank: item.rank,
      price: item.price,
      color: rankColorMap[item.rank] || "",
      delete: false,
    });
  };

  /**
   * Show delete confirmation
   */
  const handleDelete = (item) => {
    setShowForm(false);
    setFormData({
      id: item.background_price_id,
      rank: item.rank,
      price: item.price,
      color: rankColorMap[item.rank] || "",
      delete: true,
    });
  };

  /**
   * Reset form and hide
   */
  const handleDiscard = () => {
    setShowForm(false);
    setFormData(INITIAL_FORM_STATE);
  };

  /**
   * Get available ranks not yet assigned
   */
  const getAvailableRanks = () =>
    rankList.filter((rank) => !data.items.some((item) => item.rank === rank.rank));

  /**
   * Reload table data
   */
  const reloadData = async () => {
    try {
      const res = await backgroundPriceAPI.getAll(campaign.campaign_id);
      if (res.status === 200) {
        const json = await res.json();
        setData({ items: json.data, cryptoPrice: json.cryptoPrice });
      }
    } catch (error) {
      console.error("Error reloading data:", error);
    }
  };

  /**
   * Create new background price entry
   */
  const handleCreate = async (formData) => {
    setSpinner(true);
    try {
      await backgroundPriceAPI.create(campaign.campaign_id, {
        campaign_id: campaign.campaign_id,
        rank: formData.rank,
        price: formData.price,
      });
      await reloadData();
      handleDiscard();
    } catch (error) {
      console.error("Error creating background price:", error);
    } finally {
      setSpinner(false);
    }
  };

  /**
   * Update existing background price entry
   */
  const handleUpdate = async (formData) => {
    setSpinner(true);
    try {
      await backgroundPriceAPI.update(campaign.campaign_id, formData.id, {
        campaign_id: campaign.campaign_id,
        rank: formData.rank,
        price: formData.price,
      });
      await reloadData();
      handleDiscard();
    } catch (error) {
      console.error("Error updating background price:", error);
    } finally {
      setSpinner(false);
    }
  };

  /**
   * Delete background price entry
   */
  const handleDeleteConfirm = async () => {
    setSpinner(true);
    try {
      await backgroundPriceAPI.delete(campaign.campaign_id, formData.id);
      await reloadData();
      handleDiscard();
    } catch (error) {
      console.error("Error deleting background price:", error);
    } finally {
      setSpinner(false);
    }
  };

  if (showForm) {
    return (
      <>
        <Title title="Background Price" />
        <Description text={[
          "Determine the pricing levels based on the Fund Raisin NFT's background here.",
          "Note: Prices are set in AVAX. USD or any other currency values are approximate and update automatically based on current market rates."
        ]} />
        <FormCard>
          <Form
            handleSubmit={formData.id ? handleUpdate : handleCreate}
            onDiscard={handleDiscard}
            rankList={getAvailableRanks()}
            isEdit={!!formData.id}
            formData={formData}
            cryptoPrice={data.cryptoPrice}
          />
        </FormCard>
      </>
    );
  }

  if (formData.delete && formData.id) {
    return (
      <Delete
        formData={formData}
        onDiscard={handleDiscard}
        handleSubmit={handleDeleteConfirm}
      />
    );
  }

  return (
    <>
      <Title title="Background Price" />
      <Description text={[
        "Determine the pricing levels based on the Fund Raisin NFT's background here.",
        "Note: Prices are set in AVAX. USD or any other currency values are approximate and update automatically based on current market rates."
      ]} />


      {!campaign?.sc_id && (
        <div className="add-btn" onClick={handleAdd}>
          Add Background Color Price
          <img src={plus} className="add-icon" alt="Add" />
        </div>
      )}

      <Table
        data={data.items}
        onEdit={handleEdit}
        onDelete={handleDelete}
        rankColorMap={rankColorMap}
        isAllowedToEditOrDelete={!campaign?.sc_id}
        cryptoPrice={data.cryptoPrice}
      />
    </>
  );
};

export default Index;
